import React from 'react';
import { ErrorCode as FileErrorCode, useDropzone } from 'react-dropzone';
import { useTranslation } from 'react-i18next';
import styled, { css } from 'styled-components';

import type { DropzoneOptions, FileRejection } from 'react-dropzone';
import type { TFunction } from 'react-i18next';
import type { FieldError } from 'react-hook-form';
import type { IFileProxy } from './types';

import { ButtonAdd } from '../Buttons';
import { Attachment } from '../resources';
import { media } from '@/styles/theme';
import { InputDescription } from './components';

interface IFileInputProps {
  name: string;
  id?: string;
  value: IFileProxy[];
  setValue: (value: IFileProxy[]) => void;
  label?: string;
  buttonLabel?: string;
  required?: boolean;
  maxSize: number;
  maxFiles?: number;
  error?: FieldError;
  description?: string;
}

const getFileInputErrorMessage = (fileRejections: FileRejection[], maxFiles: number, t: TFunction): string[] => {
  if (fileRejections.length === 0) return [];

  if (fileRejections.some(rejection => rejection.errors.some(error => error.code === FileErrorCode.TooManyFiles)))
    return [t('commonComponents.filePicker.errors.tooMany', { maxFiles })];

  return fileRejections.map(({ file, errors }) => {
    if (errors.some(error => error.code === FileErrorCode.FileInvalidType)) {
      return t('commonComponents.filePicker.errors.badType', { name: file.name });
    }

    if (errors.some(error => error.code === FileErrorCode.FileTooLarge)) {
      return t('commonComponents.filePicker.errors.tooLarge', { name: file.name });
    }

    return t('commonComponents.filePicker.errors.generic', { name: file.name });
  });
};

const FileInput = ({ setValue, value, label, name, maxFiles = 0, ...rest }: IFileInputProps): React.ReactElement => {
  const { t } = useTranslation();
  const onDrop: DropzoneOptions['onDrop'] = files => {
    setValue([...value, ...files.map(file => ({ file, path: null, name: file.name }))]);
  };

  const { getRootProps, getInputProps, open, fileRejections, isDragAccept, isDragReject, rootRef } = useDropzone({
    noClick: true,
    onDrop,
    maxFiles: maxFiles - value.length,
    maxSize: rest.maxSize,
  });

  return (
    <fieldset>
      {label && (
        <InputLabel htmlFor={rest.id || name}>
          {rest.required && <span className="text-danger me-1">*</span>}
          {label}
        </InputLabel>
      )}
      <DropArea {...getRootProps({ isDragAccept, isDragReject })}>
        <input {...getInputProps({ id: rest.id || name, name })} />
        {(maxFiles === 0 || value.length < maxFiles) && (
          <DropAreaButtonContainer>
            <StyledButton
              size="s"
              className="drop-area-button"
              onClick={() => {
                open();
                rootRef.current?.focus();
              }}
            >
              {rest.buttonLabel || t`commonComponents.filePicker.buttonLabel`}
            </StyledButton>
            <DropAreaLabel>{t`commonComponents.filePicker.dropLabel`}</DropAreaLabel>
          </DropAreaButtonContainer>
        )}
        {value.length > 0 && (
          <AcceptedFilesContainer>
            {value.map((fileProxy, idx) => (
              <Attachment
                key={`${fileProxy.name}_${idx}`}
                fileName={fileProxy.name}
                actions={{
                  delete: () => setValue(value.filter((_, fileIdx) => idx !== fileIdx)),
                  preview: fileProxy.path ?? undefined,
                }}
              />
            ))}
          </AcceptedFilesContainer>
        )}
      </DropArea>
      {rest.description && <InputDescription>{rest.description}</InputDescription>}
      <ErrorMessage>
        {getFileInputErrorMessage(fileRejections, maxFiles, t).map((errorMessage, idx) => (
          <p key={idx}>{errorMessage}</p>
        ))}
        {rest.error && <p>{rest.error.message}</p>}
      </ErrorMessage>
    </fieldset>
  );
};

const InputLabel = styled.label`
  font-size: 16px;
  color: #374151;
  line-height: 24px;
  margin-bottom: 4px;
`;

const DropArea = styled.div<{ isDragAccept: boolean; isDragReject: boolean }>`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 8px;
  border: 1px solid #ced4da;
  border-radius: 8px;
  padding: 8px;

  ${({ isDragAccept }) =>
    isDragAccept &&
    css`
      background-color: #fff;
      border-color: #28a745;
      outline: 0;
      box-shadow: 0 0 0 0.2rem rgb(40 167 69 / 25%);
    `}

  ${({ isDragReject }) =>
    isDragReject &&
    css`
      background-color: #fff;
      border-color: #eb0000;
      outline: 0;
      box-shadow: 0 0 0 0.2rem rgb(235 0 0 / 25%);
    `}

    ${({ isDragAccept, isDragReject }) =>
    !isDragAccept &&
    !isDragReject &&
    css`
      &:focus {
        color: #495057;
        background-color: #fff;
        border-color: #80bdff;
        outline: 0;
        box-shadow: 0 0 0 0.2rem rgb(0 123 255 / 25%);
      }
    `}
`;

const DropAreaLabel = styled.p`
  display: none;
  margin: 0;

  ${media.laptop} {
    display: block;
  }
`;

const StyledButton = styled(ButtonAdd)`
  width: 100px;
  height: 34px;
`;

const DropAreaButtonContainer = styled.div`
  display: flex;
  flex-direction: column;
  padding: 16px;
  align-items: center;
`;

const AcceptedFilesContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
  justify-self: flex-end;
  width: 100%;
`;

const ErrorMessage = styled.div.attrs({
  className: 'text-danger',
})`
  min-height: 24px;
  padding-top: 4px;
  font-size: 12px;
  line-height: 19px;

  p {
    margin: 0;
  }
`;

export { FileInput };
